import { useState } from 'react';
import { PageHeader } from '@/components/layout/AppShell';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input, Field } from '@/components/ui/Input';
import { toast } from '@/components/ui/Toast';
import { useAppStore } from '@/app/store';
import { api, ApiError } from '@/lib/api';

export function SettingsPage() {
  const user = useAppStore((s) => s.user);

  return (
    <>
      <PageHeader title="Settings" description="Your account." />
      <div className="grid max-w-3xl gap-6">
        <Card>
          <CardHeader title="Profile" />
          <CardBody className="space-y-3 text-sm">
            <div className="flex items-center justify-between gap-4">
              <span className="text-fg-muted">Name</span>
              <span className="font-medium text-fg">{user?.name ?? '—'}</span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <span className="text-fg-muted">Email</span>
              <span className="font-medium text-fg">{user?.email}</span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <span className="text-fg-muted">Account type</span>
              <Badge tone={user?.kind === 'STAFF' ? 'primary' : 'neutral'}>{user?.kind.toLowerCase()}</Badge>
            </div>
          </CardBody>
        </Card>

        <ChangePasswordCard />
      </div>
    </>
  );
}

function ChangePasswordCard() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await api.changePassword(current, next);
      toast.success('Password changed');
      setCurrent('');
      setNext('');
    } catch (err) {
      toast.danger('Could not change password', err instanceof ApiError ? err.message : undefined);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardHeader title="Change password" subtitle="At least 8 characters." />
      <CardBody>
        <form className="max-w-sm space-y-4" onSubmit={submit}>
          <Field label="Current password" htmlFor="current-password">
            <Input
              id="current-password"
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              required
            />
          </Field>
          <Field label="New password" htmlFor="new-password">
            <Input
              id="new-password"
              type="password"
              autoComplete="new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              minLength={8}
              required
            />
          </Field>
          <Button type="submit" disabled={busy || !current || next.length < 8}>
            {busy ? 'Saving…' : 'Change password'}
          </Button>
        </form>
      </CardBody>
    </Card>
  );
}
